/**
 * NavigatorX App Configuration Manager
 *
 * Enterprise-grade configuration management dashboard:
 * - Feature flags with rollout controls and user targeting
 * - App settings (trial, pricing, rate limits, referral, endpoints)
 * - Force update management
 * - Maintenance mode with scheduling and bypass management
 * - Configuration history and rollback
 * - Real-time subscriptions for live updates
 *
 * @author Claude AI for TGE.LA
 * @version 2.0.0 — Added user targeting, maintenance scheduling, bypass management,
 *   rate limit editing, referral editing, endpoint editing, real-time subscriptions
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  Settings,
  Flag,
  Smartphone,
  Wrench,
  History,
  RefreshCw,
  Loader2,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  Percent,
  DollarSign,
  Shield,
  Zap,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  CalendarClock,
  UserPlus,
  UserMinus,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { Timestamp } from 'firebase/firestore';
import { auth } from '../../../utils/firebase/client';
import {
  getFeatureFlags,
  createFeatureFlag,
  updateFeatureFlag,
  toggleFeatureFlag,
  deleteFeatureFlag,
  addUsersToFlag,
  removeUsersFromFlag,
  getAppConfig,
  updateAppConfig,
  getForceUpdateConfig,
  updateForceUpdateConfig,
  enableForceUpdate,
  disableForceUpdate,
  getMaintenanceConfig,
  enableMaintenanceMode,
  disableMaintenanceMode,
  updateMaintenanceConfig,
  scheduleMaintenanceMode,
  addMaintenanceBypassUsers,
  removeMaintenanceBypassUsers,
  getConfigHistory,
  rollbackConfig,
  initializeConfigs,
  subscribeToFeatureFlags,
  subscribeToMaintenance,
  subscribeToForceUpdate,
  type FeatureFlag,
  type AppConfig,
  type ForceUpdateConfig,
  type MaintenanceConfig,
  type ConfigHistoryEntry
} from '../../../utils/services/navigatorXConfigService';

interface AppConfigManagerProps {
  embedded?: boolean;
}

type ActiveTab = 'flags' | 'app' | 'update' | 'maintenance' | 'history';

type EditableSection = 'trial' | 'pricing' | 'rateLimits' | 'referral' | 'endpoints';

type EditValues =
  | AppConfig['trial']
  | AppConfig['pricing']
  | AppConfig['rateLimits']
  | AppConfig['referral']
  | AppConfig['endpoints'];

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Operation failed';
}

function formatTimestamp(ts: unknown): string {
  if (!ts) return 'Never';
  const date = ts instanceof Timestamp ? ts.toDate() : new Date(ts as string);
  return date.toLocaleString();
}

function getHistoryDotColor(action: string): string {
  switch (action) {
    case 'create': return 'bg-green-500';
    case 'delete': return 'bg-red-500';
    case 'enable': return 'bg-blue-500';
    case 'disable': return 'bg-orange-500';
    case 'rollback': return 'bg-purple-500';
    default: return 'bg-gray-500';
  }
}

export default function AppConfigManager({ embedded = false }: AppConfigManagerProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('flags');

  // Data state
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [forceUpdate, setForceUpdate] = useState<ForceUpdateConfig | null>(null);
  const [maintenance, setMaintenance] = useState<MaintenanceConfig | null>(null);
  const [history, setHistory] = useState<ConfigHistoryEntry[]>([]);

  // UI state
  const [showNewFlag, setShowNewFlag] = useState(false);
  const [expandedFlag, setExpandedFlag] = useState<string | null>(null);
  const [newFlag, setNewFlag] = useState({
    key: '',
    name: '',
    description: '',
    enabled: false,
    rolloutPercentage: 100,
    environment: 'all' as FeatureFlag['environment'],
    tags: [] as string[],
    isCritical: false
  });

  // Edit states for app config sections
  const [editingSection, setEditingSection] = useState<EditableSection | null>(null);
  const [editValues, setEditValues] = useState<EditValues | null>(null);

  // Feature flag user targeting state
  const [flagUserInput, setFlagUserInput] = useState('');
  const [flagUserTarget, setFlagUserTarget] = useState<'enabled' | 'disabled'>('enabled');

  // Maintenance scheduling state
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleDuration, setScheduleDuration] = useState('60');
  const [scheduleMessage, setScheduleMessage] = useState('');

  // Maintenance bypass state
  const [bypassUserInput, setBypassUserInput] = useState('');

  // Maintenance message editing
  const [editingMaintenanceMessage, setEditingMaintenanceMessage] = useState(false);
  const [maintenanceMessageTitle, setMaintenanceMessageTitle] = useState('');
  const [maintenanceMessageBody, setMaintenanceMessageBody] = useState('');

  // Real-time subscription cleanup refs
  const unsubFlagsRef = useRef<(() => void) | null>(null);
  const unsubMaintenanceRef = useRef<(() => void) | null>(null);
  const unsubForceUpdateRef = useRef<(() => void) | null>(null);

  const adminId = auth.currentUser?.uid || 'unknown';

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      await initializeConfigs(adminId);

      const [flags, config, update, maint, hist] = await Promise.all([
        getFeatureFlags(),
        getAppConfig(),
        getForceUpdateConfig(),
        getMaintenanceConfig(),
        getConfigHistory({ limitCount: 50 })
      ]);

      setFeatureFlags(flags);
      setAppConfig(config);
      setForceUpdate(update);
      setMaintenance(maint);
      setHistory(hist);
    } catch (error) {
      console.error('Error loading config data:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    loadAllData();

    // Set up real-time subscriptions
    unsubFlagsRef.current = subscribeToFeatureFlags((flags) => {
      setFeatureFlags(flags);
    });
    unsubMaintenanceRef.current = subscribeToMaintenance((config) => {
      setMaintenance(config);
    });
    unsubForceUpdateRef.current = subscribeToForceUpdate((config) => {
      setForceUpdate(config);
    });

    return () => {
      unsubFlagsRef.current?.();
      unsubMaintenanceRef.current?.();
      unsubForceUpdateRef.current?.();
    };
  }, [loadAllData]);

  // ============================================================================
  // FEATURE FLAGS HANDLERS
  // ============================================================================

  const handleCreateFlag = async () => {
    if (!newFlag.key || !newFlag.name) {
      toast.error('Key and name are required');
      return;
    }

    setSaving(true);
    try {
      await createFeatureFlag({
        ...newFlag,
        enabledUserIds: [],
        disabledUserIds: [],
        enabledSubscriptionStatuses: []
      }, adminId);

      toast.success('Feature flag created');
      setShowNewFlag(false);
      setNewFlag({
        key: '',
        name: '',
        description: '',
        enabled: false,
        rolloutPercentage: 100,
        environment: 'all',
        tags: [],
        isCritical: false
      });
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFlag = async (key: string, enabled: boolean) => {
    try {
      await toggleFeatureFlag(key, enabled, adminId);
      toast.success(`Flag ${enabled ? 'enabled' : 'disabled'}`);
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdateFlagRollout = async (key: string, percentage: number) => {
    try {
      await updateFeatureFlag(key, { rolloutPercentage: percentage }, adminId);
      toast.success('Rollout updated');
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDeleteFlag = async (key: string) => {
    if (!confirm(`Delete feature flag "${key}"? This cannot be undone.`)) return;

    try {
      await deleteFeatureFlag(key, adminId);
      toast.success('Flag deleted');
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleAddUsersToFlag = async (flagKey: string) => {
    const userIds = flagUserInput.split(',').map(id => id.trim()).filter(id => id.length > 0);
    if (userIds.length === 0) {
      toast.error('Enter at least one user ID');
      return;
    }

    try {
      if (flagUserTarget === 'enabled') {
        await addUsersToFlag(flagKey, userIds, adminId);
        toast.success(`${userIds.length} user(s) added to enabled list`);
      } else {
        await updateFeatureFlag(flagKey, {
          disabledUserIds: [
            ...(featureFlags.find(f => f.key === flagKey)?.disabledUserIds || []),
            ...userIds
          ]
        }, adminId);
        toast.success(`${userIds.length} user(s) added to disabled list`);
      }
      setFlagUserInput('');
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRemoveUserFromFlag = async (flagKey: string, userId: string, listType: 'enabled' | 'disabled') => {
    try {
      if (listType === 'enabled') {
        await removeUsersFromFlag(flagKey, [userId], adminId);
      } else {
        const flag = featureFlags.find(f => f.key === flagKey);
        if (flag) {
          await updateFeatureFlag(flagKey, {
            disabledUserIds: flag.disabledUserIds.filter(id => id !== userId)
          }, adminId);
        }
      }
      toast.success('User removed');
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // ============================================================================
  // APP CONFIG HANDLERS
  // ============================================================================

  const handleSaveAppConfig = async (section: EditableSection) => {
    if (!editValues) return;

    setSaving(true);
    try {
      await updateAppConfig({ [section]: editValues }, adminId);
      toast.success('Configuration saved');
      setEditingSection(null);
      setEditValues(null);
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // FORCE UPDATE HANDLERS
  // ============================================================================

  const handleEnableForceUpdate = async () => {
    if (!forceUpdate) return;

    setSaving(true);
    try {
      await enableForceUpdate(
        'android',
        { code: forceUpdate.android.minVersionCode, name: forceUpdate.android.minVersionName },
        adminId,
        {
          title: forceUpdate.updateMessage.title,
          body: forceUpdate.updateMessage.body,
          whatsNew: forceUpdate.updateMessage.whatsNew
        }
      );
      toast.success('Force update enabled');
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDisableForceUpdate = async () => {
    setSaving(true);
    try {
      await disableForceUpdate(adminId);
      toast.success('Force update disabled');
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // MAINTENANCE HANDLERS
  // ============================================================================

  const handleToggleMaintenance = async (enabled: boolean) => {
    setSaving(true);
    try {
      if (enabled) {
        await enableMaintenanceMode(adminId, {
          message: maintenance?.message,
          estimatedDurationMinutes: 60,
          allowReadOnly: false
        });
        toast.success('Maintenance mode enabled');
      } else {
        await disableMaintenanceMode(adminId);
        toast.success('Maintenance mode disabled');
      }
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleScheduleMaintenance = async () => {
    if (!scheduleDate || !scheduleTime) {
      toast.error('Date and time are required');
      return;
    }
    const duration = parseInt(scheduleDuration, 10);
    if (isNaN(duration) || duration < 1) {
      toast.error('Duration must be a positive number');
      return;
    }

    const startTime = new Date(`${scheduleDate}T${scheduleTime}`);
    if (startTime <= new Date()) {
      toast.error('Scheduled time must be in the future');
      return;
    }

    setSaving(true);
    try {
      await scheduleMaintenanceMode(
        adminId,
        startTime,
        duration,
        scheduleMessage ? { title: 'Scheduled Maintenance', body: scheduleMessage } : undefined
      );
      toast.success(`Maintenance scheduled for ${startTime.toLocaleString()}`);
      setShowScheduleForm(false);
      setScheduleDate('');
      setScheduleTime('');
      setScheduleDuration('60');
      setScheduleMessage('');
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleAddBypassUser = async () => {
    const userIds = bypassUserInput.split(',').map(id => id.trim()).filter(id => id.length > 0);
    if (userIds.length === 0) {
      toast.error('Enter at least one user ID');
      return;
    }

    try {
      await addMaintenanceBypassUsers(userIds, adminId);
      toast.success(`${userIds.length} user(s) added to bypass list`);
      setBypassUserInput('');
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRemoveBypassUser = async (userId: string) => {
    try {
      await removeMaintenanceBypassUsers([userId], adminId);
      toast.success('User removed from bypass list');
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSaveMaintenanceMessage = async () => {
    if (!maintenanceMessageTitle.trim() || !maintenanceMessageBody.trim()) {
      toast.error('Title and body are required');
      return;
    }

    setSaving(true);
    try {
      await updateMaintenanceConfig({
        message: { title: maintenanceMessageTitle.trim(), body: maintenanceMessageBody.trim() }
      }, adminId);
      toast.success('Maintenance message updated');
      setEditingMaintenanceMessage(false);
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // HISTORY/ROLLBACK HANDLERS
  // ============================================================================

  const handleRollback = async (entryId: string) => {
    if (!confirm('Rollback to this configuration state? Current settings will be replaced.')) return;

    setSaving(true);
    try {
      await rollbackConfig(entryId, adminId);
      toast.success('Configuration rolled back');
      loadAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">App Configuration</h2>
            <p className="text-gray-500">Manage feature flags, settings, and app behavior</p>
          </div>
          <Button variant="outline" onClick={loadAllData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {/* Status Indicators */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className={maintenance?.enabled ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Maintenance</p>
                <p className={`text-lg font-bold ${maintenance?.enabled ? 'text-red-600' : 'text-green-600'}`}>
                  {maintenance?.enabled ? 'ACTIVE' : 'OFF'}
                </p>
              </div>
              <Wrench className={`h-6 w-6 ${maintenance?.enabled ? 'text-red-400' : 'text-gray-300'}`} />
            </div>
          </CardContent>
        </Card>

        <Card className={forceUpdate?.enabled ? 'border-orange-300 bg-orange-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Force Update</p>
                <p className={`text-lg font-bold ${forceUpdate?.enabled ? 'text-orange-600' : 'text-gray-600'}`}>
                  {forceUpdate?.enabled ? 'ENABLED' : 'OFF'}
                </p>
              </div>
              <Smartphone className={`h-6 w-6 ${forceUpdate?.enabled ? 'text-orange-400' : 'text-gray-300'}`} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Feature Flags</p>
                <p className="text-lg font-bold text-gray-700">{featureFlags.length}</p>
              </div>
              <Flag className="h-6 w-6 text-gray-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Trial Duration</p>
                <p className="text-lg font-bold text-gray-700">{appConfig?.trial.durationDays || 7} days</p>
              </div>
              <Clock className="h-6 w-6 text-gray-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b overflow-x-auto">
        {([
          { id: 'flags' as const, label: 'Feature Flags', icon: Flag },
          { id: 'app' as const, label: 'App Settings', icon: Settings },
          { id: 'update' as const, label: 'Force Update', icon: Smartphone },
          { id: 'maintenance' as const, label: 'Maintenance', icon: Wrench },
          { id: 'history' as const, label: 'History', icon: History }
        ]).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Feature Flags Tab */}
      {activeTab === 'flags' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">
              {featureFlags.filter(f => f.enabled).length} of {featureFlags.length} flags enabled
            </p>
            <Button onClick={() => setShowNewFlag(true)} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              New Flag
            </Button>
          </div>

          {/* New Flag Form */}
          {showNewFlag && (
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Create Feature Flag</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Key (snake_case)</Label>
                    <Input
                      value={newFlag.key}
                      onChange={(e) => setNewFlag({ ...newFlag, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                      placeholder="new_feature_enabled"
                    />
                  </div>
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={newFlag.name}
                      onChange={(e) => setNewFlag({ ...newFlag, name: e.target.value })}
                      placeholder="New Feature"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Description</Label>
                    <Input
                      value={newFlag.description}
                      onChange={(e) => setNewFlag({ ...newFlag, description: e.target.value })}
                      placeholder="What this flag controls..."
                    />
                  </div>
                  <div>
                    <Label>Environment</Label>
                    <select
                      value={newFlag.environment}
                      onChange={(e) => setNewFlag({ ...newFlag, environment: e.target.value as FeatureFlag['environment'] })}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="all">All Environments</option>
                      <option value="production">Production Only</option>
                      <option value="staging">Staging Only</option>
                      <option value="development">Development Only</option>
                    </select>
                  </div>
                  <div>
                    <Label>Rollout %</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={newFlag.rolloutPercentage}
                      onChange={(e) => setNewFlag({ ...newFlag, rolloutPercentage: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newFlag.enabled}
                        onChange={(e) => setNewFlag({ ...newFlag, enabled: e.target.checked })}
                      />
                      Enabled
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newFlag.isCritical}
                        onChange={(e) => setNewFlag({ ...newFlag, isCritical: e.target.checked })}
                      />
                      Critical
                    </label>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleCreateFlag} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                    Create
                  </Button>
                  <Button variant="outline" onClick={() => setShowNewFlag(false)}>
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Flags List */}
          {featureFlags.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Flag className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No feature flags configured</p>
                <Button onClick={() => setShowNewFlag(true)} className="mt-4">
                  <Plus className="h-4 w-4 mr-1" />
                  Create First Flag
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {featureFlags.map(flag => (
                <Card key={flag.key} className={flag.enabled ? 'border-green-200' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div
                        className="flex items-center gap-3 cursor-pointer flex-1"
                        onClick={() => setExpandedFlag(expandedFlag === flag.key ? null : flag.key)}
                      >
                        {expandedFlag === flag.key ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{flag.name}</span>
                            <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{flag.key}</code>
                            {flag.isCritical && <Badge className="bg-red-100 text-red-700">Critical</Badge>}
                            {flag.environment !== 'all' && (
                              <Badge variant="outline">{flag.environment}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{flag.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Percent className="h-4 w-4 text-gray-400" />
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={flag.rolloutPercentage}
                            onChange={(e) => handleUpdateFlagRollout(flag.key, parseInt(e.target.value) || 0)}
                            className="w-16 border rounded px-2 py-1 text-center"
                          />
                          <span className="text-gray-400">%</span>
                        </div>
                        <Switch
                          checked={flag.enabled}
                          onCheckedChange={(checked) => handleToggleFlag(flag.key, checked)}
                        />
                      </div>
                    </div>

                    {expandedFlag === flag.key && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Created</p>
                            <p>{formatTimestamp(flag.createdAt)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Updated</p>
                            <p>{formatTimestamp(flag.updatedAt)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Updated By</p>
                            <code className="text-xs">{flag.updatedBy?.substring(0, 12)}...</code>
                          </div>
                          <div>
                            <p className="text-gray-500">Tags</p>
                            <p>{flag.tags.length > 0 ? flag.tags.join(', ') : 'None'}</p>
                          </div>
                          {flag.expiresAt && (
                            <div>
                              <p className="text-gray-500">Expires</p>
                              <p>{formatTimestamp(flag.expiresAt)}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-gray-500">Subscription Targeting</p>
                            <p>{flag.enabledSubscriptionStatuses.length > 0 ? flag.enabledSubscriptionStatuses.join(', ') : 'All'}</p>
                          </div>
                        </div>

                        {/* User Targeting Section */}
                        <div className="mt-4 pt-4 border-t">
                          <h5 className="text-sm font-medium mb-3 flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            User Targeting
                          </h5>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Enabled Users ({flag.enabledUserIds.length})</p>
                              {flag.enabledUserIds.length > 0 ? (
                                <div className="space-y-1 max-h-32 overflow-y-auto">
                                  {flag.enabledUserIds.map(userId => (
                                    <div key={userId} className="flex items-center justify-between bg-green-50 rounded px-2 py-1">
                                      <code className="text-xs">{userId.substring(0, 16)}...</code>
                                      <button
                                        onClick={() => handleRemoveUserFromFlag(flag.key, userId, 'enabled')}
                                        className="text-red-400 hover:text-red-600"
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400">No targeted users</p>
                              )}
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Disabled Users ({flag.disabledUserIds.length})</p>
                              {flag.disabledUserIds.length > 0 ? (
                                <div className="space-y-1 max-h-32 overflow-y-auto">
                                  {flag.disabledUserIds.map(userId => (
                                    <div key={userId} className="flex items-center justify-between bg-red-50 rounded px-2 py-1">
                                      <code className="text-xs">{userId.substring(0, 16)}...</code>
                                      <button
                                        onClick={() => handleRemoveUserFromFlag(flag.key, userId, 'disabled')}
                                        className="text-red-400 hover:text-red-600"
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400">No excluded users</p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 mt-3">
                            <select
                              value={flagUserTarget}
                              onChange={(e) => setFlagUserTarget(e.target.value as 'enabled' | 'disabled')}
                              className="border rounded px-2 py-1.5 text-sm"
                            >
                              <option value="enabled">Add to Enabled</option>
                              <option value="disabled">Add to Disabled</option>
                            </select>
                            <Input
                              placeholder="User ID(s), comma-separated"
                              value={flagUserInput}
                              onChange={(e) => setFlagUserInput(e.target.value)}
                              className="flex-1 h-9"
                            />
                            <Button size="sm" onClick={() => handleAddUsersToFlag(flag.key)}>
                              <UserPlus className="h-4 w-4 mr-1" />
                              Add
                            </Button>
                          </div>
                        </div>

                        <div className="flex gap-2 mt-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(flag.key);
                              toast.success('Key copied');
                            }}
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            Copy Key
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={() => handleDeleteFlag(flag.key)}
                            disabled={flag.isCritical}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* App Settings Tab */}
      {activeTab === 'app' && appConfig && (
        <div className="grid gap-4">
          {/* Trial Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Trial Settings</CardTitle>
                  <CardDescription>Configure free trial behavior</CardDescription>
                </div>
                {editingSection !== 'trial' ? (
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditingSection('trial');
                    setEditValues({ ...appConfig.trial });
                  }}>
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleSaveAppConfig('trial')} disabled={saving}>
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditingSection(null); setEditValues(null); }}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingSection === 'trial' && editValues ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Duration (days)</Label>
                    <Input
                      type="number"
                      value={(editValues as AppConfig['trial']).durationDays}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['trial']), durationDays: parseInt(e.target.value) || 7 })}
                    />
                  </div>
                  <div>
                    <Label>Warning Days Before</Label>
                    <Input
                      type="number"
                      value={(editValues as AppConfig['trial']).warningDaysBefore}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['trial']), warningDaysBefore: parseInt(e.target.value) || 2 })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={(editValues as AppConfig['trial']).enabled}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['trial']), enabled: e.target.checked })}
                    />
                    <Label>Trials Enabled</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={(editValues as AppConfig['trial']).showExpiryWarnings}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['trial']), showExpiryWarnings: e.target.checked })}
                    />
                    <Label>Show Expiry Warnings</Label>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Duration</p>
                    <p className="font-medium">{appConfig.trial.durationDays} days</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status</p>
                    <Badge className={appConfig.trial.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>
                      {appConfig.trial.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-gray-500">Warnings</p>
                    <p className="font-medium">{appConfig.trial.showExpiryWarnings ? `${appConfig.trial.warningDaysBefore} days before` : 'Off'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Features</p>
                    <p className="font-medium">{appConfig.trial.features.length} enabled</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pricing Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Pricing Settings</CardTitle>
                  <CardDescription>Configure subscription pricing display</CardDescription>
                </div>
                {editingSection !== 'pricing' ? (
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditingSection('pricing');
                    setEditValues({ ...appConfig.pricing });
                  }}>
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleSaveAppConfig('pricing')} disabled={saving}>
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditingSection(null); setEditValues(null); }}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingSection === 'pricing' && editValues ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Monthly Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={(editValues as AppConfig['pricing']).monthlyPrice}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['pricing']), monthlyPrice: parseFloat(e.target.value) || 9.99 })}
                    />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <select
                      value={(editValues as AppConfig['pricing']).currency}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['pricing']), currency: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Label>Promo Message (optional)</Label>
                    <Input
                      value={(editValues as AppConfig['pricing']).promoMessage || ''}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['pricing']), promoMessage: e.target.value })}
                      placeholder="Limited time offer..."
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Monthly Price</p>
                    <p className="font-medium text-lg">${appConfig.pricing.monthlyPrice}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Currency</p>
                    <p className="font-medium">{appConfig.pricing.currency}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Display Pricing</p>
                    <Badge className={appConfig.pricing.displayPricing ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>
                      {appConfig.pricing.displayPricing ? 'Shown' : 'Hidden'}
                    </Badge>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rate Limits */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Rate Limits</CardTitle>
                  <CardDescription>API and feature rate limiting</CardDescription>
                </div>
                {editingSection !== 'rateLimits' ? (
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditingSection('rateLimits');
                    setEditValues({ ...appConfig.rateLimits });
                  }}>
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleSaveAppConfig('rateLimits')} disabled={saving}>
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditingSection(null); setEditValues(null); }}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingSection === 'rateLimits' && editValues ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>API Requests / min</Label>
                    <Input
                      type="number"
                      value={(editValues as AppConfig['rateLimits']).apiRequestsPerMinute}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['rateLimits']), apiRequestsPerMinute: parseInt(e.target.value) || 60 })}
                    />
                  </div>
                  <div>
                    <Label>Well Searches / min</Label>
                    <Input
                      type="number"
                      value={(editValues as AppConfig['rateLimits']).wellSearchesPerMinute}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['rateLimits']), wellSearchesPerMinute: parseInt(e.target.value) || 30 })}
                    />
                  </div>
                  <div>
                    <Label>Navigations / hr</Label>
                    <Input
                      type="number"
                      value={(editValues as AppConfig['rateLimits']).navigationsPerHour}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['rateLimits']), navigationsPerHour: parseInt(e.target.value) || 50 })}
                    />
                  </div>
                  <div>
                    <Label>Hazard Reports / day</Label>
                    <Input
                      type="number"
                      value={(editValues as AppConfig['rateLimits']).hazardReportsPerDay}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['rateLimits']), hazardReportsPerDay: parseInt(e.target.value) || 20 })}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">API Requests</p>
                    <p className="font-medium">{appConfig.rateLimits.apiRequestsPerMinute}/min</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Well Searches</p>
                    <p className="font-medium">{appConfig.rateLimits.wellSearchesPerMinute}/min</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Navigations</p>
                    <p className="font-medium">{appConfig.rateLimits.navigationsPerHour}/hr</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Hazard Reports</p>
                    <p className="font-medium">{appConfig.rateLimits.hazardReportsPerDay}/day</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Referral Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Referral Program</CardTitle>
                  <CardDescription>Configure referral rewards</CardDescription>
                </div>
                {editingSection !== 'referral' ? (
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditingSection('referral');
                    setEditValues({ ...appConfig.referral });
                  }}>
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleSaveAppConfig('referral')} disabled={saving}>
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditingSection(null); setEditValues(null); }}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingSection === 'referral' && editValues ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={(editValues as AppConfig['referral']).enabled}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['referral']), enabled: e.target.checked })}
                    />
                    <Label>Referral Program Enabled</Label>
                  </div>
                  <div>
                    <Label>Free Months / Referral</Label>
                    <Input
                      type="number"
                      value={(editValues as AppConfig['referral']).freeMonthsPerReferral}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['referral']), freeMonthsPerReferral: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <div>
                    <Label>Required Paid Months</Label>
                    <Input
                      type="number"
                      value={(editValues as AppConfig['referral']).requiredPaidMonths}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['referral']), requiredPaidMonths: parseInt(e.target.value) || 2 })}
                    />
                  </div>
                  <div>
                    <Label>Max Referrals / User</Label>
                    <Input
                      type="number"
                      value={(editValues as AppConfig['referral']).maxReferralsPerUser}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['referral']), maxReferralsPerUser: parseInt(e.target.value) || 10 })}
                    />
                  </div>
                  <div>
                    <Label>Code Prefix</Label>
                    <Input
                      value={(editValues as AppConfig['referral']).codePrefix}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['referral']), codePrefix: e.target.value.toUpperCase() })}
                      placeholder="NAV"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Status</p>
                    <Badge className={appConfig.referral.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>
                      {appConfig.referral.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-gray-500">Free Months/Referral</p>
                    <p className="font-medium">{appConfig.referral.freeMonthsPerReferral}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Required Paid Months</p>
                    <p className="font-medium">{appConfig.referral.requiredPaidMonths}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Max Referrals</p>
                    <p className="font-medium">{appConfig.referral.maxReferralsPerUser}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Endpoints */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">API Endpoints</CardTitle>
                  <CardDescription>Oklahoma well database and caching configuration</CardDescription>
                </div>
                {editingSection !== 'endpoints' ? (
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditingSection('endpoints');
                    setEditValues({ ...appConfig.endpoints });
                  }}>
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleSaveAppConfig('endpoints')} disabled={saving}>
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditingSection(null); setEditValues(null); }}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingSection === 'endpoints' && editValues ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Oklahoma Well Database URL</Label>
                    <Input
                      value={(editValues as AppConfig['endpoints']).occApiUrl}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['endpoints']), occApiUrl: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Cache TTL (minutes)</Label>
                    <Input
                      type="number"
                      value={(editValues as AppConfig['endpoints']).cacheTtlMinutes}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['endpoints']), cacheTtlMinutes: parseInt(e.target.value) || 30 })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={(editValues as AppConfig['endpoints']).useProductionOcc}
                      onChange={(e) => setEditValues({ ...(editValues as AppConfig['endpoints']), useProductionOcc: e.target.checked })}
                    />
                    <Label>Use Production Database</Label>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="col-span-2">
                    <p className="text-gray-500">Oklahoma Well Database URL</p>
                    <p className="font-medium text-xs font-mono truncate">{appConfig.endpoints.occApiUrl}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Cache TTL</p>
                    <p className="font-medium">{appConfig.endpoints.cacheTtlMinutes} min</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Force Update Tab */}
      {activeTab === 'update' && forceUpdate && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Force Update Configuration</CardTitle>
                <CardDescription>Require users to update to a minimum version</CardDescription>
              </div>
              <div className="flex items-center gap-4">
                <span className={`text-sm font-medium ${forceUpdate.enabled ? 'text-orange-600' : 'text-gray-500'}`}>
                  {forceUpdate.enabled ? 'ENABLED' : 'DISABLED'}
                </span>
                <Switch
                  checked={forceUpdate.enabled}
                  onCheckedChange={(checked) => checked ? handleEnableForceUpdate() : handleDisableForceUpdate()}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Android Config */}
              <div className="space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  Android
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Min Version Code</p>
                    <p className="font-medium">{forceUpdate.android.minVersionCode}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Min Version Name</p>
                    <p className="font-medium">{forceUpdate.android.minVersionName}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Grace Period</p>
                    <p className="font-medium">{forceUpdate.android.gracePeriodHours}h</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Allow Skip</p>
                    <Badge className={forceUpdate.android.allowSkip ? 'bg-green-100' : 'bg-red-100'}>
                      {forceUpdate.android.allowSkip ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* iOS Config */}
              <div className="space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  iOS (Future)
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Min Build Number</p>
                    <p className="font-medium">{forceUpdate.ios.minBuildNumber}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Min Version</p>
                    <p className="font-medium">{forceUpdate.ios.minVersionString}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Update Message */}
            <div className="mt-6 pt-6 border-t">
              <h4 className="font-medium mb-4">Update Message</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium">{forceUpdate.updateMessage.title}</p>
                <p className="text-sm text-gray-600 mt-1">{forceUpdate.updateMessage.body}</p>
                {forceUpdate.updateMessage.whatsNew.length > 0 && (
                  <ul className="mt-2 text-sm text-gray-600">
                    {forceUpdate.updateMessage.whatsNew.map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <p className="text-xs text-gray-400 mt-4">
              Last updated: {formatTimestamp(forceUpdate.updatedAt)} by {forceUpdate.updatedBy?.substring(0, 12)}...
            </p>
          </CardContent>
        </Card>
      )}

      {/* Maintenance Tab */}
      {activeTab === 'maintenance' && maintenance && (
        <div className="space-y-4">
          <Card className={maintenance.enabled ? 'border-red-300' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Wrench className={maintenance.enabled ? 'text-red-500' : ''} />
                    Maintenance Mode
                  </CardTitle>
                  <CardDescription>
                    {maintenance.enabled
                      ? 'App is in maintenance mode - users cannot access'
                      : 'Enable to prevent user access during updates'}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-sm font-medium ${maintenance.enabled ? 'text-red-600' : 'text-gray-500'}`}>
                    {maintenance.enabled ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                  <Switch
                    checked={maintenance.enabled}
                    onCheckedChange={handleToggleMaintenance}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {maintenance.enabled && maintenance.startedAt && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-red-700">
                    <AlertTriangle className="h-4 w-4 inline mr-2" />
                    Maintenance started: {formatTimestamp(maintenance.startedAt)}
                    {maintenance.estimatedEndTime && (
                      <span className="ml-2">| Estimated end: {formatTimestamp(maintenance.estimatedEndTime)}</span>
                    )}
                  </p>
                </div>
              )}

              {maintenance.scheduledStartTime && !maintenance.enabled && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-700">
                    <CalendarClock className="h-4 w-4 inline mr-2" />
                    Scheduled: {formatTimestamp(maintenance.scheduledStartTime)}
                    {maintenance.estimatedEndTime && (
                      <span className="ml-2">| Estimated end: {formatTimestamp(maintenance.estimatedEndTime)}</span>
                    )}
                  </p>
                </div>
              )}

              <div className="space-y-6">
                {/* Message Preview / Edit */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">User Message</h4>
                    {!editingMaintenanceMessage ? (
                      <Button size="sm" variant="outline" onClick={() => {
                        setEditingMaintenanceMessage(true);
                        setMaintenanceMessageTitle(maintenance.message.title);
                        setMaintenanceMessageBody(maintenance.message.body);
                      }}>
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSaveMaintenanceMessage} disabled={saving}>
                          <Save className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingMaintenanceMessage(false)}>
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                  {editingMaintenanceMessage ? (
                    <div className="space-y-3">
                      <div>
                        <Label>Title</Label>
                        <Input
                          value={maintenanceMessageTitle}
                          onChange={(e) => setMaintenanceMessageTitle(e.target.value)}
                          placeholder="Maintenance in Progress"
                        />
                      </div>
                      <div>
                        <Label>Body</Label>
                        <Input
                          value={maintenanceMessageBody}
                          onChange={(e) => setMaintenanceMessageBody(e.target.value)}
                          placeholder="We're updating the app. Please check back soon."
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-100 rounded-lg p-4">
                      <p className="font-medium">{maintenance.message.title}</p>
                      <p className="text-sm text-gray-600 mt-1">{maintenance.message.body}</p>
                    </div>
                  )}
                </div>

                {/* Options */}
                <div>
                  <h4 className="font-medium mb-2">Options</h4>
                  <div className="flex gap-4">
                    {maintenance.showCountdown && <Badge variant="outline">Countdown Enabled</Badge>}
                    {maintenance.allowReadOnly && <Badge variant="outline">Read-only Access</Badge>}
                    {!maintenance.showCountdown && !maintenance.allowReadOnly && (
                      <p className="text-sm text-gray-400">No special options enabled</p>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-400 mt-6">
                Last updated: {formatTimestamp(maintenance.updatedAt)} by {maintenance.updatedBy?.substring(0, 12)}...
              </p>
            </CardContent>
          </Card>

          {/* Schedule Maintenance */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <CalendarClock className="h-5 w-5" />
                    Schedule Maintenance
                  </CardTitle>
                  <CardDescription>Pre-announce a maintenance window</CardDescription>
                </div>
                <Button
                  size="sm"
                  variant={showScheduleForm ? 'default' : 'outline'}
                  onClick={() => setShowScheduleForm(!showScheduleForm)}
                >
                  {showScheduleForm ? <X className="h-4 w-4 mr-1" /> : <CalendarClock className="h-4 w-4 mr-1" />}
                  {showScheduleForm ? 'Cancel' : 'Schedule'}
                </Button>
              </div>
            </CardHeader>
            {showScheduleForm && (
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Time</Label>
                    <Input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Duration (minutes)</Label>
                    <Input
                      type="number"
                      value={scheduleDuration}
                      onChange={(e) => setScheduleDuration(e.target.value)}
                      className="mt-1"
                      min="1"
                    />
                  </div>
                  <div>
                    <Label>Message (optional)</Label>
                    <Input
                      value={scheduleMessage}
                      onChange={(e) => setScheduleMessage(e.target.value)}
                      placeholder="System upgrade in progress..."
                      className="mt-1"
                    />
                  </div>
                </div>
                <Button onClick={handleScheduleMaintenance} disabled={saving} className="mt-4">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarClock className="h-4 w-4 mr-2" />}
                  Schedule Maintenance
                </Button>
              </CardContent>
            )}
          </Card>

          {/* Bypass Management */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Bypass Management
              </CardTitle>
              <CardDescription>Users and domains that can access the app during maintenance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Bypass User IDs */}
                <div>
                  <p className="text-sm font-medium mb-2">Bypass Users ({maintenance.bypassUserIds.length})</p>
                  {maintenance.bypassUserIds.length > 0 ? (
                    <div className="space-y-1 max-h-40 overflow-y-auto mb-3">
                      {maintenance.bypassUserIds.map(userId => (
                        <div key={userId} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1">
                          <code className="text-xs">{userId.substring(0, 20)}...</code>
                          <button
                            onClick={() => handleRemoveBypassUser(userId)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mb-3">No bypass users</p>
                  )}
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="User ID(s)"
                      value={bypassUserInput}
                      onChange={(e) => setBypassUserInput(e.target.value)}
                      className="h-8 text-xs"
                    />
                    <Button size="sm" onClick={handleAddBypassUser}>
                      <UserPlus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Bypass Email Domains */}
                <div>
                  <p className="text-sm font-medium mb-2">Bypass Domains</p>
                  {maintenance.bypassEmailDomains.length > 0 ? (
                    <div className="space-y-1">
                      {maintenance.bypassEmailDomains.map(domain => (
                        <div key={domain} className="bg-gray-50 rounded px-2 py-1">
                          <code className="text-xs">{domain}</code>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No bypass domains</p>
                  )}
                </div>

                {/* Bypass Subscription Statuses */}
                <div>
                  <p className="text-sm font-medium mb-2">Bypass Subscription Statuses</p>
                  {maintenance.bypassSubscriptionStatuses.length > 0 ? (
                    <div className="space-y-1">
                      {maintenance.bypassSubscriptionStatuses.map(status => (
                        <Badge key={status} variant="outline" className="mr-1">{status}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No subscription bypass</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle>Configuration History</CardTitle>
            <CardDescription>Track all configuration changes with rollback capability</CardDescription>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No configuration changes recorded</p>
            ) : (
              <div className="space-y-2">
                {history.map(entry => (
                  <div key={entry.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${getHistoryDotColor(entry.action)}`} />
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{entry.configType}</Badge>
                          <span className="font-medium capitalize">{entry.action}</span>
                          {entry.configKey && (
                            <code className="text-xs bg-gray-200 px-1 rounded">{entry.configKey}</code>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatTimestamp(entry.changedAt)} by {entry.changedBy?.substring(0, 8)}...
                          {entry.reason && <span className="ml-2">• {entry.reason}</span>}
                        </p>
                      </div>
                    </div>
                    {entry.previousValue && entry.action !== 'create' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRollback(entry.id!)}
                        disabled={saving}
                      >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Rollback
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
